import { createException } from '@/utils/monitoring.utils';
import * as Sentry from '@sentry/vue';
import { onBeforeUnmount, onMounted } from 'vue';

// Note: the global `Window.fcWidget` type is declared in
// components/merchant/NavbarComponent.vue.

let isScriptLoaded = false;
let isWidgetInitialized = false;

const CLASS_NAME = 'fc_widget_frame';

export interface IFreshchatConfig {
  token: string;
  host: string;
  siteId: string;
}

function loadScript(): Promise<void> {
  return new Promise((resolve) => {
    if (isScriptLoaded) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'freshchat-js-sdk';
    script.src = 'https://wchat.freshchat.com/js/widget.js';
    script.async = true;
    script.onload = () => {
      isScriptLoaded = true;
      resolve();
    };
    script.onerror = (event, source, lineno, colno, error) => {
      createException(error ?? new Error('Failed to load Freshchat script'), {
        extra: { event, source, lineno, colno },
      });
      resolve(); // Resolve even on error to prevent blocking further execution
    };

    document.head.appendChild(script);
  });
}

function initWidget(config: IFreshchatConfig) {
  if (isWidgetInitialized) return;

  // @ts-ignore - init is not part of the shared fcWidget type declaration
  window.fcWidget.init({
    token: config.token,
    host: config.host,
    siteId: config.siteId,
    config: {
      cssNames: {
        widget: CLASS_NAME,
      },
    },
  });

  isWidgetInitialized = true;
}

export async function setupFreshchat(config: IFreshchatConfig) {
  await loadScript();
  if (isScriptLoaded) initWidget(config);
}

export const isFreshChatWidgetIllegalInvocation = (event: Sentry.ErrorEvent) => {
  const exception = event.exception?.values?.[0];
  const frames = exception?.stacktrace?.frames ?? [];

  const isFromWidget = frames.some(
    (f) => f.filename?.includes('/js/widget.js') || f.filename?.includes('wchat.freshchat.com'),
  );
  const isIllegalInvocation = exception?.type === 'TypeError' && exception?.value === 'Illegal invocation';

  return isFromWidget && isIllegalInvocation;
};

export function offsetFreshchatOnScrollToBottom() {
  let widget: HTMLElement | null = null;
  let normalBottom = '';

  onMounted(() => window.addEventListener('scroll', handleScroll));
  onBeforeUnmount(() => window.removeEventListener('scroll', handleScroll));

  function handleScroll() {
    const scrollHeight = document.documentElement.scrollHeight - 70;

    if (widget == null) {
      widget = document.querySelector<HTMLElement>(`.${CLASS_NAME}`);
      if (widget == null) return;
      normalBottom = widget.style.bottom;
    }

    const offset = Math.max(0, window.scrollY + window.innerHeight - scrollHeight);

    if (offset == 0) {
      if (widget.style.bottom !== normalBottom) widget.style.bottom = normalBottom;
      return;
    }

    widget.style.bottom = `calc(${normalBottom} + ${offset}px)`;
  }
}

export function toggleFreshchatVisibility() {
  const mediaQuery = window.matchMedia('(max-width: 580px)');

  onMounted(() => {
    if (mediaQuery.matches && window.fcWidget) window.fcWidget.hide();
    mediaQuery.addEventListener('change', toggleVisibility);
  });

  onBeforeUnmount(() => mediaQuery.removeEventListener('change', toggleVisibility));

  function toggleVisibility() {
    if (!window.fcWidget) return;
    if (mediaQuery.matches) window.fcWidget.hide();
    else window.fcWidget.show();
  }
}

// Style element id used to force-hide the Freshchat launcher.
const HIDE_STYLE_ID = 'freshchat-force-hide';

// Covers the common Freshchat launcher/widget containers across SDK versions:
// the configured widget class, the default launcher iframe, and the wrapper div.
const FRESHCHAT_HIDE_SELECTORS = [
  `.${CLASS_NAME}`,
  '#fc_frame',
  '#fc-widget',
  '#freshchat-widget',
  'iframe[title="Freshchat"]',
  'iframe[src*="freshchat"]',
].join(', ');

/**
 * Hide the Freshchat widget while a component is mounted (e.g. a full-screen
 * checkout step where the floating chat bubble would overlap the UI), and
 * restore it on unmount.
 *
 * The `fcWidget.hide()` API only collapses the chat window (not the launcher
 * bubble), and the exact launcher element/id varies across Freshchat SDK
 * versions. To be robust we inject a stylesheet that force-hides every known
 * launcher selector with `!important` (beating the widget's inline styles), and
 * remove it again on unmount.
 */
export function hideFreshchatWhileOpen() {
  onMounted(() => {
    try {
      window.fcWidget?.hide();
    } catch {
      // Widget not ready
    }

    if (!document.getElementById(HIDE_STYLE_ID)) {
      const style = document.createElement('style');

      style.id = HIDE_STYLE_ID;
      style.textContent = `
        ${FRESHCHAT_HIDE_SELECTORS} {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `;

      document.head.appendChild(style);
    }
  });

  onBeforeUnmount(() => {
    document.getElementById(HIDE_STYLE_ID)?.remove();

    try {
      window.fcWidget?.show();
    } catch {
      // Widget not ready
    }
  });
}
